import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'
import type { TablePage, TableRequest } from './softsee'

/**
 * Securitee - /api/v2/securitee/*. Perechile vechi sunt in
 * `SecuriteeController` (API). Perioada (`from`/`to`, yyyy-mm-dd) e cea din
 * `SecuriteeAllDevices.dataXhr` (securitee.js): azi, pana la alegerea din
 * calendar.
 */

export interface Period {
  from: string
  to: string
}

export function today(): string {
  return daysAgo(0)
}

/** Ziua de acum `n` zile (YYYY-MM-DD, ora locala), ca `today()`. */
export function daysAgo(n: number): string {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function params(r: Omit<TableRequest, 'list'>, extra: Record<string, string> = {}): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  for (const [k, v] of Object.entries(extra)) p.set(k, v)
  return p
}

/**
 * Starea antivirusului masinii, cum o trimite `SecuriteeSource::AV_*`.
 *
 * `unknown` NU e `off`: masina n-are niciun rand in
 * `it_opteemum_security_software_status` - agent vechi, masina care n-a mai
 * raportat, sau Linux/mac, unde Windows Security Center nu exista. Pana pe 24
 * septembrie 2026 coloana citea tabelele prototipului Bitdefender, pe care nu le
 * mai umple nimeni, si arata "uninstalled" pe toata flota.
 */
export type SecuriteeAntivirusState = 'on' | 'stale' | 'off' | 'unknown'

export interface SecuriteeDeviceRow {
  ID: number
  DT_RowId: string
  Tip: number
  device_type: string
  device_icon: string
  Denumire: string
  ownerId: number
  Nume: string | null
  Prenume: string | null
  UserLogat: string | null
  DeviceGroup: string | null
  USB: number
  Antivirus: SecuriteeAntivirusState
  /** Produsele vazute de Windows Security Center, virgula intre ele; gol cand nu e raportat niciunul. */
  AntivirusName: string
  Files: number
  FilesSize: string
  DataAccesareDM: string | null
  [key: string]: unknown
  /** Eticheta GravityZone, numai la firmele legate (`SecuriteeDevices::devices`). */
  GravityZone?: import('./securiteeGravityZone').GzStatus
}

export function devicesTableQuery(r: Omit<TableRequest, 'list'> & Period) {
  return queryOptions({
    queryKey: ['securitee', 'devices', r],
    queryFn: () => apiGet<TablePage<SecuriteeDeviceRow> & { gravityzone?: boolean }>(`/securitee/devices-table?${params(r, { from: r.from, to: r.to })}`),
  })
}

export interface SecuriteeEventRow {
  ID: number
  DT_RowId: string
  device_icon: string
  device_type: string
  device_name: string
  OwnerID: number | null
  username: string | null
  file_name: string
  file_destination: string
  file_source: string
  device_date: string
  server_date: string
  [key: string]: unknown
}

export function eventsTableQuery(r: Omit<TableRequest, 'list'> & Period) {
  return queryOptions({
    queryKey: ['securitee', 'events', r],
    queryFn: () => apiGet<TablePage<SecuriteeEventRow>>(`/securitee/events-table?${params(r, { from: r.from, to: r.to })}`),
  })
}

/** Un rand din `Detections::table` (API) - o alerta Wazuh pe un aparat al firmei. */
export interface SecuriteeDetectionRow {
  DT_RowId: string
  detected_at: string
  device_id: number
  device_name: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  rule_level: number
  rule_id: string
  title: string
  mitre: string
  tactic: string
  /** incidentul in care a intrat detectia; null = niciunul */
  incident_id?: number | null
  [key: string]: unknown
}

/** Coada SOC pe "All companies" (docs XDR 03, decizia 7): coloana "Company" apare numai atunci. */
export interface AllCompanies {
  all_companies?: boolean
}

/** Optiunile dropdownurilor din bara de filtre: regulile si tehnicile MITRE de pe firma. */
export interface DetectionFacets {
  rules: string[]
  mitre: string[]
}

/** Ce a ales omul in bara de filtre; toate restrang impreuna, toate server-side. */
export interface DetectionFilterValues {
  severities: string[]
  rules: string[]
  mitre: string[]
  from: string
  to: string
}

export const EMPTY_DETECTION_FILTERS: DetectionFilterValues = { severities: [], rules: [], mitre: [], from: '', to: '' }

export function detectionFilterCount(f: DetectionFilterValues): number {
  return f.severities.length + f.rules.length + f.mitre.length + (f.from ? 1 : 0) + (f.to ? 1 : 0)
}

/** Filtrele ca parametri in plus peste cererea DataTables; listele merg cu virgula. */
function detectionFilterParams(f: DetectionFilterValues): Record<string, string> {
  const e: Record<string, string> = {}
  if (f.severities.length) e.severities = f.severities.join(',')
  if (f.rules.length) e.rules = f.rules.join(',')
  if (f.mitre.length) e.mitre = f.mitre.join(',')
  if (f.from) e.from = f.from
  if (f.to) e.to = f.to
  return e
}

/** `?device=` din legatura spre aparat: un parametru in plus, numai cand e ales. */
function deviceExtra(device: number): Record<string, string> {
  return device > 0 ? { device: String(device) } : {}
}

export function detectionsTableQuery(r: Omit<TableRequest, 'list'>, filters: DetectionFilterValues = EMPTY_DETECTION_FILTERS, device = 0) {
  const extra = { ...detectionFilterParams(filters), ...deviceExtra(device) }
  return queryOptions({
    queryKey: ['securitee', 'detections', r, extra],
    queryFn: () => apiGet<TablePage<SecuriteeDetectionRow> & AllCompanies & { facets?: DetectionFacets }>(`/securitee/detections-table?${params(r, extra)}`),
  })
}

/** Un rand din `EdrEvents::table` (API) - un eveniment EDR brut de pe un aparat al firmei. */
export interface SecuriteeEdrEventRow {
  DT_RowId: string
  event_time: string
  device_id: number
  device_name: string
  channel: string
  event_id: number
  text: string
  [key: string]: unknown
}

/** Optiunile dropdownurilor din bara EDR events: canalele si ID-urile de eveniment de pe firma. */
export interface EdrEventFacets {
  channels: string[]
  eventIds: number[]
}

/** Ce a ales omul in bara de filtre EDR events; toate restrang impreuna, toate server-side. */
export interface EdrEventFilterValues {
  channels: string[]
  eventIds: string[]
}

export const EMPTY_EDR_EVENT_FILTERS: EdrEventFilterValues = { channels: [], eventIds: [] }

export function edrEventFilterCount(f: EdrEventFilterValues): number {
  return f.channels.length + f.eventIds.length
}

function edrEventFilterParams(f: EdrEventFilterValues): Record<string, string> {
  const e: Record<string, string> = {}
  if (f.channels.length) e.channels = f.channels.join(',')
  if (f.eventIds.length) e.eventIds = f.eventIds.join(',')
  return e
}

/** Fereastra din jurul unui moment (`?around=`), din setarea XDR a firmei aparatului; momentele in UTC. */
export interface EdrEventWindow {
  around: string
  from: string
  to: string
  before: number
  after: number
}

/** `around`: momentul YYYY-MM-DDTHH:MM:SS din "EDR events around 09:00"; API-ul pune fereastra in locul perioadei. */
export function edrEventsTableQuery(r: Omit<TableRequest, 'list'> & Period, filters: EdrEventFilterValues = EMPTY_EDR_EVENT_FILTERS, device = 0, around?: string) {
  const extra = { from: r.from, to: r.to, ...edrEventFilterParams(filters), ...deviceExtra(device), ...(around ? { around } : {}) }
  return queryOptions({
    queryKey: ['securitee', 'edr-events', r, extra],
    queryFn: () => apiGet<TablePage<SecuriteeEdrEventRow> & AllCompanies & { facets?: EdrEventFacets; window?: EdrEventWindow }>(`/securitee/edr-events-table?${params(r, extra)}`),
  })
}

export function deviceEdrEventsQuery(id: number, r: Omit<TableRequest, 'list'> & Period, filters: EdrEventFilterValues = EMPTY_EDR_EVENT_FILTERS) {
  const extra = { from: r.from, to: r.to, ...edrEventFilterParams(filters) }
  return queryOptions({
    queryKey: ['securitee', 'device', id, 'edr-events', r, extra],
    queryFn: () => apiGet<TablePage<SecuriteeEdrEventRow> & { facets?: EdrEventFacets }>(`/securitee/devices/${id}/edr-events/table?${params(r, extra)}`),
  })
}

export interface SecuriteeTabs {
  /** `name`: capul paginii aparatului (`/securitee/all_devices/$id`). */
  device: { id: number; name?: string }
  tabs: { key: string; label: string }[]
  /**
   * Cheia raportului de scanare pornit din caseta Diagnostic (`security_scan`),
   * sau sirul gol cand omul n-are `securitee.security_scan.read` - si atunci
   * caseta nu se deseneaza. NU e o adresa: pana pe 24 septembrie 2026 era
   * adresa portalului vechi, iar butonul iesea din /app in gol.
   */
  security_scan: string
}

export function deviceTabsQuery(id: number) {
  return queryOptions({ queryKey: ['securitee', 'device', id, 'tabs'], queryFn: () => apiGet<SecuriteeTabs>(`/securitee/devices/${id}/tabs`) })
}

export interface SecuriteeSummary {
  /**
   * Un cuvant: Enabled / Outdated / Disabled / Not reported. "Not reported" e
   * masina fara niciun rand de antivirus - nu e acelasi lucru cu "nu are", si
   * de-aia nu mai spune Uninstalled (`SecuriteeDevices::AV_WORDS`).
   */
  antivirusStatus: string
  files: number
  files_size: string
  documents: number
  documents_size: string
  loggedUserSummary: { LoggedUser: string; Files: number; Size: string }[]
  /** Configuratia de baza citita de agent (CyFun PR.PS-01.1); null = n-a raportat-o. */
  baseline: SecuriteeBaseline | null
}

export interface SecuriteeBaseline {
  updated: string
  checks: { key: string; label: string; state: 'ok' | 'fail' | 'unknown' | 'na'; text: string }[]
}

export function deviceSummaryQuery(id: number, period: Period) {
  return queryOptions({
    queryKey: ['securitee', 'device', id, 'summary', period],
    queryFn: () => apiGet<SecuriteeSummary>(`/securitee/devices/${id}/summary?from=${period.from}&to=${period.to}`),
  })
}

export interface SecuriteeFileRow {
  ID: number
  DT_RowId: string
  username: string
  file_name: string
  file_destination: string
  file_size: string
  file_source: string
  device_date: string
  server_date: string
  [key: string]: unknown
}

export function deviceFilesQuery(id: number, r: Omit<TableRequest, 'list'> & Period & { fileType: number; fileSource: number }) {
  return queryOptions({
    queryKey: ['securitee', 'device', id, 'files', r],
    queryFn: () =>
      apiGet<TablePage<SecuriteeFileRow>>(
        `/securitee/devices/${id}/files/table?${params(r, { from: r.from, to: r.to, fileType: String(r.fileType), fileSource: String(r.fileSource) })}`,
      ),
  })
}

export interface SecuriteeLogRow {
  ID: number
  DT_RowId: string
  event_date: string
  event_message: string
  Nume: string | null
  Prenume: string | null
  [key: string]: unknown
}

/** Fila Detections a aparatului - `Detections::table` filtrat pe aparat. */
export function deviceDetectionsQuery(id: number, r: Omit<TableRequest, 'list'>) {
  return queryOptions({
    queryKey: ['securitee', 'device', id, 'detections', r],
    queryFn: () => apiGet<TablePage<SecuriteeDetectionRow>>(`/securitee/devices/${id}/detections/table?${params(r)}`),
  })
}

export function deviceEventsQuery(id: number, r: Omit<TableRequest, 'list'>) {
  return queryOptions({
    queryKey: ['securitee', 'device', id, 'events', r],
    queryFn: () => apiGet<TablePage<SecuriteeLogRow>>(`/securitee/devices/${id}/events/table?${params(r)}`),
  })
}

export type SettingField = 'Usb' | 'WebUploads' | 'EmailScanning' | 'PublicShares' | 'AdminShares' | 'CryptoLocker' | 'FilesCreationBlocker' | 'RegistryImmunizer'

export interface SecuriteeSettings {
  device: { id: number }
  settings: Record<SettingField, number | null>
  exists: boolean
}

export function deviceSettingsQuery(id: number) {
  return queryOptions({ queryKey: ['securitee', 'device', id, 'settings'], queryFn: () => apiGet<SecuriteeSettings>(`/securitee/devices/${id}/settings`) })
}

export function saveDeviceSettings(id: number, settings: Partial<Record<SettingField, number>>) {
  const fields: Record<string, number> = {}
  for (const [k, v] of Object.entries(settings)) fields[`settings[${k}]`] = v
  return apiPost<{ status: string; message: string }>(`/securitee/devices/${id}/settings`, fields)
}

export function saveManyDeviceSettings(devices: number[], form: Record<string, string>) {
  return apiPost<{ status: string; message: string }>('/securitee/devices/settings', { devices, ...form })
}

export interface SecuriteeSelection {
  files: number
  files_size: string
  documents: number
  documents_size: string
  protected_devices: number
  all_devices: number
}

/**
 * Casetele widgetului multi-select, PE PERIOADA aleasa pe ecran.
 *
 * Pana pe 25 septembrie 2026 capatul asta nu primea perioada, si serverul aducea
 * tot istoricul masinilor bifate in memoria PHP ca sa le numere rand cu rand -
 * pe o flota mare, o bifa care nu se mai termina. Acum se numara in baza, si
 * perioada pleaca cu cererea, ca la lista si la filele randului desfacut.
 */
export function selectionQuery(devices: number[], period: Period) {
  return queryOptions({
    queryKey: ['securitee', 'selection', devices, period],
    queryFn: () => apiPost<SecuriteeSelection>('/securitee/devices/selection', { device: devices, from: period.from, to: period.to }),
  })
}

/**
 * Izolarea aparatului la ransomware - `DeviceIsolationController`
 * (`/api/v2/devices/{id}/isolation`). Starea vine din ultima comanda ISO/RLS
 * din coada si raspunsul agentului la ea (`Isolation::derive`). Oprirea NU e
 * aici: e `shutdownDevice` din `api/devices.ts` (remote-ops/shutdown).
 */
export type IsolationState = 'none' | 'isolating' | 'isolated' | 'releasing'

/** Nivelul izolarii (argumentul lui ISO): full = tot, internal = fara LAN, external = fara internet. */
export type IsolationLevel = 'full' | 'internal' | 'external'

export interface DeviceIsolation {
  state: IsolationState
  since: string | null
  /** Nivelul starii de acum; null cand aparatul nu e izolat. */
  level?: IsolationLevel | null
  last_error: { command: 'ISO' | 'RLS'; message: string } | null
  can_isolate: boolean
  can_shutdown: boolean
}

export function deviceIsolationQuery(id: number) {
  return queryOptions({
    queryKey: ['securitee', 'device', id, 'isolation'],
    queryFn: () => apiGet<DeviceIsolation>(`/devices/${id}/isolation`),
  })
}

export function isolateDevice(id: number, level: IsolationLevel = 'full') {
  // totala pleaca fara `level`, ca inainte de niveluri
  return apiPost<DeviceIsolation & { ok: boolean }>(`/devices/${id}/isolation/isolate`, level === 'full' ? { device: id } : { device: id, level })
}

export function releaseDevice(id: number) {
  return apiPost<DeviceIsolation & { ok: boolean }>(`/devices/${id}/isolation/release`, { device: id })
}

/**
 * Procesele suspecte la ransomware, raportate de Microsoft Defender (Controlled
 * Folder Access 1123 blocked / 1124 audited) - `DeviceRansomwareSuspectsController`
 * (`/api/v2/devices/{id}/ransomware/suspects`). `running` = PID-urile din ultimul
 * raport de aplicatii active cu aceeasi cale; gol = nu ruleaza.
 */
export interface RansomwareSuspect {
  path: string
  user: string
  count: number
  last_seen: string
  actions: Array<'blocked' | 'audited'>
  running: Array<{ pid: number; name: string }>
}

export interface RansomwareSuspects {
  since: string
  cfa_ready: boolean
  cfa_mode: 0 | 1 | 2
  report_at: string | null
  can_stop: boolean
  suspects: RansomwareSuspect[]
}

export function ransomwareSuspectsQuery(id: number) {
  return queryOptions({
    queryKey: ['securitee', 'device', id, 'ransomware-suspects'],
    queryFn: () => apiGet<RansomwareSuspects>(`/devices/${id}/ransomware/suspects`),
  })
}

export function stopSuspectProcess(id: number, path: string, pid: number) {
  return apiPost<RansomwareSuspects & { ok: boolean }>(`/devices/${id}/ransomware/suspects/stop`, { path, pid })
}

/**
 * Controlled Folder Access pe un aparat: `mode` null = ce are firma
 * (`companyMode`); 0 Off, 1 Audit, 2 Block. Capatul: `SecuriteeCfaController`.
 */
export type DeviceCfa = { ready: boolean; canWrite: boolean; mode: 0 | 1 | 2 | null; companyMode: 0 | 1 | 2 }

export function deviceCfaQuery(id: number) {
  return queryOptions({
    queryKey: ['securitee', 'device', id, 'cfa'],
    queryFn: () => apiGet<DeviceCfa>(`/securitee/devices/${id}/cfa`),
  })
}

/** `''` = aparatul se intoarce la setarea firmei. */
export function saveDeviceCfa(id: number, mode: '' | 0 | 1 | 2) {
  return apiPost<DeviceCfa & { ok: boolean; message: string }>(`/securitee/devices/${id}/cfa`, { mode })
}

/** Securitee > Incidents - un rand din `Incidents::row()` (API). */
export interface SecuriteeIncidentRow {
  DT_RowId: string
  ID: number
  /** Firma incidentului; pe "All companies" difera de rand la rand. */
  company_id?: number
  company_name?: string
  device_id: number
  device_name: string
  status: IncidentStatus
  severity: string
  max_level: number
  title: string
  detections: number
  first_detected_at: string
  last_detected_at: string
  /** Tichetul Easeedesk; starea incidentului e a lui. */
  ticket_id: number | null
  /** Numele starii tichetului (New, In progress, Resolved...). */
  ticket_status: string
  /** Tichetul inca se lucreaza (stare activa). */
  ticket_open?: boolean
  /** Omul tichetului ("Ana Pop"); '' = nimeni. */
  ticket_assignee?: string
  ticket_assignee_id?: number | null
  /** Starea in care pleaca tichetul la "False positive" (Dispatching, sursa XDR); '' = ramane deschis. */
  fp_ticket_status?: string
  /** Medium+ fara tichet de 5 minute; motivul in ticket_error. */
  ticket_missing?: boolean
  ticket_error?: string
  /** Inchis din coada ca "False positive" (numai incidentele fara tichet). */
  false_positive?: boolean
  /** Pana cand (UTC) se mai poate da Undo pe "False positive"; null = nu se mai poate. */
  undo_until?: string | null
  [key: string]: unknown
}

export type IncidentStatus = 'open' | 'closed'

/** open (implicit), closed, all. */
export type IncidentFilter = 'open' | 'closed' | 'all'

export interface SecuriteeIncidentDetail {
  incident: SecuriteeIncidentRow
  detections: { ID: number; detected_at: string; rule_id: string; rule_level: number; severity: string; title: string; mitre: string }[]
  /** `easeedesk.tickets.create`: butonul Open ticket. */
  can_ticket: boolean
  /** "Take" din panou: tichet deschis al altcuiva / al nimanui, preluarea pornita pe tipul lui. */
  can_take?: boolean
  /** `securitee.detections.edit`: False positive; cu tichet, si `can_false_positive_ticket` (mutarea tichetului). */
  can_false_positive?: boolean
  can_false_positive_ticket?: boolean
  /** Respond de aici merge numai pe firma din antet; altfel, din tichetul incidentului. */
  respond_here?: boolean
  /** Actiunea recomandata a celei mai grave detectii; `action` null = nimic de facut pe aparat. */
  recommended?: { action: string | null; why: string; detection_id: number; detection: string; target?: string; auto_block?: AutoBlockNote } | null
}

/** `incident` > 0: numai incidentul asta, oricare i-ar fi starea (legatura "Incident #N" din Detections). */
export function incidentsTableQuery(r: Omit<TableRequest, 'list'>, status: IncidentFilter, device = 0, incident = 0) {
  const extra = { status, ...deviceExtra(device), ...(incident > 0 ? { incident: String(incident) } : {}) }
  return queryOptions({
    queryKey: ['securitee', 'incidents', r, status, device, incident],
    queryFn: () => apiGet<TablePage<SecuriteeIncidentRow> & AllCompanies & { can_false_positive?: boolean }>(`/securitee/incidents-table?${params(r, extra)}`),
  })
}

export function incidentQuery(id: number) {
  return queryOptions({
    queryKey: ['securitee', 'incident', id],
    queryFn: () => apiGet<SecuriteeIncidentDetail>(`/securitee/incidents/${id}`),
  })
}

/** O actiune de raspuns pe aparatul incidentului, cu pasii ei (`ResponseActions::steps`). */
export interface IncidentAction {
  ID: number
  action: string
  label: string
  sent_at: string
  sent_by: string
  picked_up: boolean
  /** `no_answer`: preluata, fara raspuns de un sfert de ora - nu mai e pe drum */
  result: 'waiting' | 'running' | 'applied' | 'failed' | 'expired' | 'rejected' | 'no_answer'
  result_at: string | null
  /** Raspunsul agentului (sau de ce a picat). */
  detail: string
  /** Respond din tichet: detectia din care a pornit actiunea (null cand vine din alta parte). */
  detection_id?: number | null
  detection_title?: string
  ticket_id?: number | null
  /** Blocarea / deblocarea automata AbuseIPDB (ph4 AutoBlock); orele, UTC. */
  auto?: { source: 'abuseipdb'; score: number | null; unblock_due: string | null; unblocked_at: string | null } | null
}

export type IncidentActions = { actions: IncidentAction[]; can_unblock?: boolean }

/** "Block IP" recomandat pe un IP pe care AbuseIPDB (ph4 AutoBlock) il tine deja blocat pe aparat. */
export type AutoBlockNote = { source: 'abuseipdb'; score: number | null; unblock_due: string }

/** Textul comun al incidentului si al filei XDR: ce s-a blocat, cat, si ce mai e de verificat. */
export function autoBlockText(ip: string, a: AutoBlockNote): string {
  return `Blocked automatically: ${ip} (AbuseIPDB${a.score !== null ? ` ${a.score}%` : ''}) until ${a.unblock_due} UTC. Now check the account and where the attempts come from.`
}

/** Fluxul XDR oprit: `forward` = evenimente netrimise spre Wazuh, `read` = alerte necitite. */
export interface XdrFlowProblem {
  /** forward / read = fluxul Wazuh; native = motorul OptimumDesk */
  part: 'forward' | 'read' | 'native'
  minutes: number
}

/** Un aparat care a aruncat evenimente peste plafoane in ultimele 24 de ore (it_edr_dropped). */
export interface XdrDroppedDevice {
  device_id: number
  device_name: string
  /** lipsesc la un API mai vechi */
  company_id?: number
  company_name?: string
  /** peste plafonul de evenimente pe minut */
  rate: number
  /** cu coada plina */
  queue: number
}

/** "Details" pe un aparat din banda: GET /securitee/xdr/dropped/{id} (it_edr_drop_minutes, it_edr_dropped). */
export interface XdrDroppedDetail {
  /** pe 24 de ore, cele mai multe intai */
  channels: { channel: string; rate: number; queue: number }[]
  /** cele mai pline minute pe 7 zile; `minute` = 'YYYY-MM-DD HH:MM:00' UTC */
  minutes: { minute: string; channel: string; rate: number; queue: number }[]
  /** 7 zile, de la cea mai veche; [] pentru un aparat fara nimic aruncat */
  days: { day: string; events: number }[]
}

export function xdrDroppedQuery(device: number) {
  return queryOptions({
    queryKey: ['securitee', 'xdr', 'dropped', device],
    queryFn: () => apiGet<XdrDroppedDetail>(`/securitee/xdr/dropped/${device}`),
  })
}

/** Mediana si p90 in secunde; null sub 5 masuratori ("too few"). */
export interface SocMetricStat {
  count: number
  median: number | null
  p90: number | null
}

export interface SocNoisyRule {
  rule_id: string
  name: string
  title: string
  incidents: number
  false_positives: number
  rate: number
}

/** GET /securitee/soc-metrics - masurile cozii SOC pe 30 de zile (docs XDR 03, decizia 11). */
export interface SocMetrics {
  window_days: number
  mtta: SocMetricStat
  mttr: SocMetricStat
  false_positives: { incidents: number; false_positives: number; rate: number | null }
  untouched: number
  noisy_rules: SocNoisyRule[]
}

export function socMetricsQuery() {
  return queryOptions({
    queryKey: ['securitee', 'soc-metrics'],
    queryFn: () => apiGet<SocMetrics>('/securitee/soc-metrics'),
    refetchInterval: 60_000,
  })
}

/** Se reciteste din minut in minut: ecranul SOC sta deschis toata tura. */
export function xdrHealthQuery() {
  return queryOptions({
    queryKey: ['securitee', 'xdr', 'health'],
    // `dropped` lipseste la un API mai vechi
    queryFn: () => apiGet<{ problems: XdrFlowProblem[]; dropped?: XdrDroppedDevice[] }>('/securitee/xdr/health'),
    refetchInterval: 60_000,
  })
}

export function incidentActionsQuery(id: number) {
  return queryOptions({
    queryKey: ['securitee', 'incident', id, 'actions'],
    queryFn: () => apiGet<IncidentActions>(`/securitee/incidents/${id}/actions`),
  })
}

/** "Unblock now" pe o blocare automata AbuseIPDB: UIP ca omul; intoarce banda noua. */
export function unblockIncidentAutoBlock(id: number, actionId: number): Promise<IncidentActions> {
  return apiPost(`/securitee/incidents/${id}/actions/${actionId}/unblock`, {})
}

/** Campul unui eveniment EDR (Windows `EventData`), in ordinea din eveniment. */
export interface EdrEventField {
  name: string
  value: string
}

/** Evenimentul EDR din care a pornit o detectie. */
export interface EdrEvent {
  channel: string
  event_id: number
  provider: string
  computer: string
  event_time: string
  data: EdrEventField[]
}

export interface TicketXdrDetection {
  ID: number
  incident_id: number | null
  ticket_id: number | null
  /** E a incidentului acestui tichet (in "Workstation, 30 days" se amesteca si altele). */
  current: boolean
  detected_at: string
  rule_id: string
  rule_level: number
  severity: string
  title: string
  mitre: string
  mitre_tactics: string
  mitre_techniques: string
  event: EdrEvent | null
  /** Ce e de facut pe aparat dupa regula (`RecommendedAction`); `action` null = nimic pe aparat. */
  recommended: { action: TicketRespondAction | null; why: string; target?: string; auto_block?: AutoBlockNote }
}

/** incident = detectiile incidentului tichetului; device = tot ce a venit de pe aparat in `days` zile. */
export type TicketXdrScope = 'incident' | 'device'

/** Fila "XDR" a tichetului - `GET /easeedesk/tickets/{id}/xdr`. */
export interface TicketXdrPage {
  ticket: number
  scope: TicketXdrScope
  days: number
  limit: number
  incident: SecuriteeIncidentRow
  detections: TicketXdrDetection[]
  /** "Suppress similar": citirea detectiilor + dreptul de a lucra tichetul. */
  can_suppress?: boolean
}

export function ticketXdrQuery(ticket: number, scope: TicketXdrScope) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', ticket, 'xdr', scope],
    queryFn: () => apiGet<TicketXdrPage>(`/easeedesk/tickets/${ticket}/xdr?scope=${scope}`),
  })
}

/** Banda "Response actions" a filei XDR: ce s-a facut pe aparatul incidentului tichetului. */
export function ticketXdrActionsQuery(ticket: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', ticket, 'xdr', 'actions'],
    queryFn: () => apiGet<{ actions: IncidentAction[] }>(`/easeedesk/tickets/${ticket}/xdr/actions`),
  })
}

/** Un rand din "Ce s-a intamplat": eveniment Windows, detectie sau actiune de raspuns (UTC). */
export interface TicketXdrTimelineItem {
  at: string
  kind: 'event' | 'detection' | 'action'
  text: string
  /** detectia: gravitatea si daca e cea deschisa */
  severity?: string
  current?: boolean
  /** actiunea: cine a dat-o si cum s-a terminat */
  by?: string
  result?: string
}

/** Cronologia unei detectii - `GET /easeedesk/tickets/{id}/xdr/timeline?detection=ID`. */
export interface TicketXdrTimeline {
  from: string
  to: string
  items: TicketXdrTimelineItem[]
  /** au fost mai mult de 200 de evenimente Windows in fereastra */
  truncated: boolean
}

export function ticketXdrTimelineQuery(ticket: number, detection: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', ticket, 'xdr', 'timeline', detection],
    queryFn: () => apiGet<TicketXdrTimeline>(`/easeedesk/tickets/${ticket}/xdr/timeline?detection=${detection}`),
  })
}

/**
 * O conexiune a unui proces din lant (`it_edr_connections`); `private` = adresa
 * din LAN. Socurile spre aceeasi adresa sunt o linie: `sockets` = cate.
 */
export interface XdrConnection {
  proto: string
  remote_ip: string
  remote_port: number
  state: string
  first_seen: string
  last_seen: string
  private: boolean
  sockets: number
}

/** Un proces din lantul unei detectii (`it_edr_processes`); orele in UTC. */
export interface XdrProcess {
  pid: number
  name: string
  path: string
  cmdline: string
  user: string
  started_at: string
  exited_at: string | null
  sha256: string
  signer: string
  /** null = necunoscut: agentul nu trimite semnatura - nu inseamna nesemnat */
  signed: boolean | null
  /** procesul scurt, mort inainte de instantaneul agentului: luat din evenimentul de pornire (4688 / Sysmon 1) */
  from_event?: boolean
  /** numai procesele din lant (nu copiii) */
  connections?: XdrConnection[]
}

/** Lantul proceselor unei detectii - `GET /easeedesk/tickets/{id}/xdr/chain?detection=ID`. */
export interface TicketXdrChain {
  /** cum s-a gasit procesul evenimentului; null = detectia n-are proces */
  how: 'pid' | 'path' | 'event' | null
  /** de la radacina pana la procesul evenimentului (ultimul) */
  chain: XdrProcess[]
  parent_unknown: boolean
  children: XdrProcess[]
}

/** De unde se citesc lantul si "Unde mai e?": fila XDR a tichetului, sau panoul incidentului (si fara tichet). */
export type XdrSource = { ticket: number } | { incident: number }

function xdrBase(from: XdrSource): { key: (string | number)[]; path: string } {
  return 'ticket' in from
    ? { key: ['easeedesk', 'ticket', from.ticket, 'xdr'], path: `/easeedesk/tickets/${from.ticket}/xdr` }
    : { key: ['securitee', 'incident', from.incident], path: `/securitee/incidents/${from.incident}` }
}

export function xdrChainQuery(from: XdrSource, detection: number) {
  const { key, path } = xdrBase(from)
  return queryOptions({
    queryKey: [...key, 'chain', detection],
    queryFn: () => apiGet<TicketXdrChain>(`${path}/chain?detection=${detection}`),
  })
}

/** Ce s-ar suprima din detectia unui tichet Fals pozitiv (docs XDR 02, F4). */
export interface TicketXdrSuppressPreview {
  rule_id: string
  channel: string
  event_id: number
  field: string
  value: string
  /** implicit 90 */
  days: number
  /** o suprimare identica deja activa - se prelungeste */
  existing: { ID: number; expires_at: string } | null
}

export function ticketXdrSuppressQuery(ticket: number, detection: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', ticket, 'xdr', 'suppress', detection],
    queryFn: () => apiGet<TicketXdrSuppressPreview>(`/easeedesk/tickets/${ticket}/xdr/suppress?detection=${detection}`),
  })
}

/** Evenimentele de acelasi fel nu mai pleaca spre Wazuh, pe firma tichetului, pana la expirare. */
export function suppressTicketXdr(ticket: number, detection: number, reason: string, days: number): Promise<{ ID: number; expires_at: string; extended: boolean }> {
  return apiPost(`/easeedesk/tickets/${ticket}/xdr/suppress`, { detection_id: detection, reason, days })
}

/** "Unde mai e?" (`TicketXdr::sweep`, docs XDR 03, decizia 9): ce se cauta. */
export type XdrSweepKind = 'sha256' | 'ip' | 'path'

/** Aparatele firmei incidentului pe care a aparut valoarea, cel mai recent intai. */
export interface TicketXdrSweep {
  kind: XdrSweepKind
  value: string
  /** de cand se cauta: 30 de zile inaintea incidentului (UTC) */
  since: string
  truncated: boolean
  devices: {
    device_id: number
    device_name: string
    first_seen: string
    last_seen: string
    hits: number
    /** aparatul incidentului */
    current: boolean
  }[]
}

export function xdrSweepQuery(from: XdrSource, kind: XdrSweepKind, value: string) {
  const { key, path } = xdrBase(from)
  return queryOptions({
    queryKey: [...key, 'sweep', kind, value],
    queryFn: () => apiGet<TicketXdrSweep>(`${path}/sweep?kind=${kind}&value=${encodeURIComponent(value)}`),
  })
}

/** Actiunile Respond din tichet (`TicketXdr::ACTIONS`). */
export type TicketRespondAction =
  | 'isolate'
  | 'isolate_internal'
  | 'isolate_external'
  | 'release'
  | 'lock'
  | 'logoff'
  | 'shutdown'
  | 'block_ip'
  | 'unblock_ip'
  | 'disable_account'
  | 'enable_account'

/** Cine e logat pe aparat (`Sessions::presentSession`), cat ii trebuie lui Log off. */
export interface TicketRespondSession {
  id: string
  user_name: string
  domain: string
  login_type: string
  logged_off: boolean
}

/** Panoul Respond al tichetului - `GET /easeedesk/tickets/{id}/xdr/respond`. */
export interface TicketRespondPanel {
  /** Incidentul inca primeste actiuni: tichetul e activ (nici Resolved, nici Closed). */
  open: boolean
  device: { id: number; name: string; server: boolean }
  isolation: { state: 'none' | 'isolating' | 'isolated' | 'releasing'; level: string | null; since: string | null; last_error: string | null }
  /** `contain`: Block IP / Disable account si inversele - dreptul izolarii, pe un agent care le are (nu macOS). */
  can: { isolate: boolean; lock: boolean; logoff: boolean; shutdown: boolean; contain: boolean }
  sessions: TicketRespondSession[]
}

export function ticketRespondPanelQuery(ticket: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', ticket, 'xdr', 'respond'],
    queryFn: () => apiGet<TicketRespondPanel>(`/easeedesk/tickets/${ticket}/xdr/respond`),
  })
}

/** Ce se trimite: actiunea, detectia din care porneste si, pe un server, numele lui scris. */
export interface TicketRespondRequest {
  action: TicketRespondAction
  detection_id: number
  confirm?: string
  username?: string
  session_id?: string
  /** Adresa (Block / Unblock IP) sau contul (Disable / Enable account). */
  target?: string
}

/**
 * Respond din tichet: aparatul il alege serverul (al incidentului), nu cererea.
 * Campurile nefolosite pleaca goale - serverul le citeste ca lipsa.
 */
export function ticketRespond(ticket: number, request: TicketRespondRequest): Promise<{ ok: boolean; message?: string }> {
  return apiPost(`/easeedesk/tickets/${ticket}/xdr/respond`, {
    action: request.action,
    detection_id: request.detection_id,
    confirm: request.confirm ?? '',
    username: request.username ?? '',
    session_id: request.session_id ?? '',
    ...(request.target !== undefined ? { target: request.target } : {}),
  })
}

/** Inchide ca fals pozitiv un incident FARA tichet (cel cu tichet se inchide din tichet). */
/** `ticket_status`: starea in care a plecat tichetul ('' = a ramas deschis); lipseste fara tichet. */
export function closeIncidentFalsePositive(id: number): Promise<{ ok: boolean; undo_minutes: number; ticket_status?: string }> {
  return apiPost(`/securitee/incidents/${id}/false-positive`, {})
}

/** Undo pe "False positive", cat tine fereastra din XDR settings. */
export function undoIncidentFalsePositive(id: number): Promise<{ ok: boolean; incident_id: number; merged: number }> {
  return apiPost(`/securitee/incidents/${id}/false-positive/undo`, {})
}

/** "Open ticket" (Low; Medium+ primesc tichet automat). */
export function openIncidentTicket(id: number): Promise<{ ticket_id: number }> {
  return apiPost(`/securitee/incidents/${id}/ticket`, {})
}

/** Parametrii DataTables + filtrele, pentru celelalte module Securitee (AD accounts). */
export { params as tableParams }
